import { linkOptions } from '@tanstack/react-router';

import type {
  LibraryItemOutcome,
  WorkflowView,
} from '@/lib/api/generated/model';
import { workflowDestination } from '@/features/workflows';

/** Where spec 104 puts an installed item of each kind, inside the workspace. */
const WORKSPACE_FOLDER = { agent: '.agents', skill: '.skills' } as const;

/**
 * Where an outcome leads, or nowhere (spec 105 FR-019 to FR-021).
 *
 * A link is a promise that there is something to see, so only what the install
 * actually added or updated carries one: an item that was already installed
 * needs no trip, and a refused one has nothing at the other end.
 *
 * A workflow's destination is not decided here. `workflowDestination` exists to
 * be the one place that knows a published workflow opens its board and a draft
 * opens its builder, and its own comment says every surface must ask it rather
 * than re-derive the rule. Without the workflow the install created — which the
 * result carries — there is nothing to ask it about, so the outcome stays text.
 *
 * An agent or a skill leads to the folder it landed in, on the Files route. The
 * HTML preview panel of spec 140 deliberately does not apply here: that panel
 * previews a single task artifact, whereas an install outcome names the folder
 * an item was written into, so there would be no file for it to render. The
 * Library therefore keeps the Files-route destination it has always had.
 */
export function outcomeDestination(
  outcome: LibraryItemOutcome,
  workspaceId: string,
  workflow: WorkflowView | null | undefined,
) {
  if (outcome.outcome !== 'added' && outcome.outcome !== 'updated') {
    return undefined;
  }
  if (outcome.kind === 'workflow') {
    return workflow ? workflowDestination(workflow, workspaceId) : undefined;
  }
  return linkOptions({
    to: '/workspaces/$workspaceId/files',
    params: { workspaceId },
    search: {
      path: `${WORKSPACE_FOLDER[outcome.kind]}/${outcome.key}`,
      view: 'preview' as const,
    },
  });
}
